import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Toast } from 'primereact/toast';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { useSchoolDetailsStore } from '../../../stateManagers/hooks/useSchoolDetailsStore';
import useClassesStore from '../../../stateManagers/store/useClassesStore';
// TODO: adjust these import paths to wherever they actually live

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface Teacher {
  id: number;
  name: string;
  email_id: string;
  gender: string;
  profile_picture_url?: string;
  contact_no?: string;
}

// Matches the ACTUAL shape returned by Go's GetCombinedFormTeachers aggregation:
// { id, school_id, session_id, session, teacher_id, teacher_name, class_ids,
//   classes: [{class_id, class_name}], sections, created_at, updated_at }
interface CombinedFormTeacherEntry {
  id: string;               // the combined_form_teachers document's own _id — used for delete
  teacher_id: number;
  teacher_name: string;
  class_ids: string[];      // ObjectID hex strings — must match useClassesStore's ClassOption.id
  classes: { class_id: string; class_name: string }[];
  sections?: string[];      // flat across all assigned classes — see note above
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared styles (mirrors AttendanceSetupSettings conventions)
// ─────────────────────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  fontSize: '13px',
  outline: 'none',
  background: '#fff',
  color: '#1e293b',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  fontSize: '12px',
  fontWeight: '600',
  color: '#475569',
  marginBottom: '5px',
  display: 'block',
  letterSpacing: '0.03em',
  textTransform: 'uppercase',
};

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

const Card: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <div style={{
    background: '#fff', borderRadius: '16px', padding: '24px', marginBottom: '20px',
    boxShadow: '0 1px 6px rgba(15,23,42,0.07)', border: '1px solid #e2e8f0', ...style,
  }}>
    {children}
  </div>
);

const SectionHeader: React.FC<{
  iconClass: string; iconBg: string; title: string;
  description: string; controls?: React.ReactNode;
}> = ({ iconClass, iconBg, title, description, controls }) => (
  <div style={{
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: '20px', paddingBottom: '16px', borderBottom: '2px solid #f1f5f9',
    flexWrap: 'wrap', gap: '12px',
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div style={{
        width: '42px', height: '42px', borderRadius: '11px',
        background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <i className={iconClass} style={{ fontSize: '18px', color: '#fff' }} />
      </div>
      <div>
        <h2 style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a', margin: 0 }}>{title}</h2>
        <p style={{ fontSize: '12px', color: '#64748b', margin: '3px 0 0', lineHeight: 1.5 }}>{description}</p>
      </div>
    </div>
    {controls && (
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>{controls}</div>
    )}
  </div>
);

const InfoBanner: React.FC<{
  color?: string; bg?: string; border?: string; icon?: string; children: React.ReactNode;
}> = ({ color = '#1e40af', bg = '#eff6ff', border = '#bfdbfe', icon = 'pi pi-info-circle', children }) => (
  <div style={{ background: bg, borderRadius: '10px', padding: '12px 16px', border: `1px solid ${border}`, display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
    <i className={icon} style={{ color, fontSize: '15px', marginTop: '1px', flexShrink: 0 }} />
    <p style={{ fontSize: '12px', color, margin: 0, lineHeight: 1.6 }}>{children}</p>
  </div>
);

// Avatar with initials fallback — safely handles undefined/null/empty name
const Avatar: React.FC<{ name: string; url?: string; size?: number }> = ({ name, url, size = 36 }) => {
  const safeName = (name ?? '').trim();
  const initials = safeName
    ? safeName.split(' ').filter(Boolean).map(w => w[0]).join('').toUpperCase().slice(0, 2)
    : '?';
  const colors = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4'];
  const color = colors[(safeName.charCodeAt(0) || 0) % colors.length];
  return url ? (
    <img src={url} alt={safeName || 'Avatar'} style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
  ) : (
    <div style={{
      width: size, height: size, borderRadius: '50%', background: color,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.35, fontWeight: '700', color: '#fff', flexShrink: 0,
    }}>
      {initials}
    </div>
  );
};

// Pill chip for classes / sections
const ClassPill: React.FC<{ label: string; onRemove?: () => void; tone?: 'blue' | 'amber' }> = ({ label, onRemove, tone = 'blue' }) => {
  const palette = tone === 'blue'
    ? { bg: '#eff6ff', border: '#bfdbfe', color: '#1e40af', removeColor: '#93c5fd' }
    : { bg: '#fffbeb', border: '#fde68a', color: '#92400e', removeColor: '#fcd34d' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '5px',
      background: palette.bg, border: `1px solid ${palette.border}`, borderRadius: '20px',
      padding: '3px 10px', fontSize: '12px', fontWeight: '600', color: palette.color,
    }}>
      {label}
      {onRemove && (
        <button onClick={onRemove} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: 0,
          color: palette.removeColor, fontSize: '13px', lineHeight: 1, display: 'flex',
        }}>
          <i className="pi pi-times" style={{ fontSize: '10px' }} />
        </button>
      )}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

const CombinedFormTeacherSettings: React.FC = () => {
  const toast = useRef<Toast>(null);

  // ── Current session (needed because the backend requires session_id + session) ──
  // schoolDetails is expected to already be populated by whatever handles auth
  // middleware elsewhere in the app — this component does not fetch it itself.
  const schoolDetails = useSchoolDetailsStore(state => state.schoolDetails);
  const schoolDetailsLoading = useSchoolDetailsStore(state => state.loading);
  const currentSession = schoolDetails?.current_session ?? null;

  // ── Classes now come from the shared store instead of a local fetch ────────
  const classOptions = useClassesStore(state => state.classOptions);
  const fetchClassOptions = useClassesStore(state => state.fetchClasses);

  // ── Data lists ──────────────────────────────────────────────────────────
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [entries, setEntries]   = useState<CombinedFormTeacherEntry[]>([]);

  // ── Loading states ───────────────────────────────────────────────────────
  const [loadingTeachers, setLoadingTeachers] = useState(true);
  const [loadingClasses,  setLoadingClasses]  = useState(true);
  const [loadingEntries,  setLoadingEntries]  = useState(true);

  // ── Form state ────────────────────────────────────────────────────────────
  const [selectedTeacher,  setSelectedTeacher]  = useState<Teacher | null>(null);
  const [selectedClassIds, setSelectedClassIds] = useState<string[]>([]);
  // Sections chosen per class (UI-level only). Backend only stores one flat
  // `sections` array for the whole assignment, so on save we merge every
  // class's chosen sections into a single deduped list.
  const [selectedSections, setSelectedSections] = useState<Record<string, string[]>>({});
  const [saving, setSaving] = useState(false);

  // ── Teacher search ────────────────────────────────────────────────────────
  const [teacherSearch, setTeacherSearch] = useState('');
  const [showTeacherDropdown, setShowTeacherDropdown] = useState(false);
  const teacherRef = useRef<HTMLDivElement>(null);

  // ── Delete state ─────────────────────────────────────────────────────────
  const [deletingKey, setDeletingKey] = useState<string | null>(null);

  // ── Close dropdown on outside click ─────────────────────────────────────
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (teacherRef.current && !teacherRef.current.contains(e.target as Node)) {
        setShowTeacherDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Fetch teachers (all, no pagination limit needed for search dropdown) ─
  // Guard: drop any records without a usable name so they never reach Avatar / filter.
  const fetchTeachers = useCallback(async () => {
    setLoadingTeachers(true);
    try {
      const res = await apiClient.get('/teachers', { params: { limit: 200 } });
      setTeachers((res.data?.data ?? []).filter((t: any) => t?.name));
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load teachers', life: 4000 });
    } finally {
      setLoadingTeachers(false);
    }
  }, []);

  // ── Fetch classes via the shared classes store (session-aware, cached) ────
  const loadClasses = useCallback(async () => {
    setLoadingClasses(true);
    try {
      await fetchClassOptions();
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load classes', life: 4000 });
    } finally {
      setLoadingClasses(false);
    }
  }, [fetchClassOptions]);

  // ── Fetch existing combined form teacher entries ──────────────────────────
  // Route fixed to match Go's RegisterCombinedFormTeacherRoutes: GET /api/combined-form-teachers
  // Scoped to the current session so old-session assignments don't show up.
  // Guard: normalize missing teacher_name (orphaned join) so Avatar never receives undefined.
  const fetchEntries = useCallback(async () => {
    setLoadingEntries(true);
    try {
      const res = await apiClient.get('/api/combined-form-teachers', {
        params: currentSession?.id ? { session_id: currentSession.id } : {},
      });
      if (res.data?.status === 'success') {
        setEntries(
          (res.data?.data ?? []).map((e: any) => ({
            ...e,
            teacher_name: e.teacher_name || 'Unknown Teacher',
          }))
        );
      }
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load combined form teachers', life: 4000 });
    } finally {
      setLoadingEntries(false);
    }
  }, [currentSession?.id]);

  useEffect(() => {
    fetchTeachers();
    loadClasses();
    fetchEntries();
  }, [fetchTeachers, loadClasses, fetchEntries]);

  // ── Filtered teacher list for dropdown ───────────────────────────────────
  const filteredTeachers = teachers.filter(t =>
    t.name.toLowerCase().includes(teacherSearch.toLowerCase()) ||
    t.email_id?.toLowerCase().includes(teacherSearch.toLowerCase())
  );

  // ── Helper functions for section validation ──────────────────────────────
  const needsSectionSelection = (classId: string): boolean => {
    const cls = classOptions.find(c => c.id === classId);
    if (!cls) return false;
    return cls.sections && cls.sections.length > 0;
  };

  const hasSectionsSelected = (classId: string): boolean => {
    return !!(selectedSections[classId] && selectedSections[classId].length > 0);
  };

  const getClassesMissingSections = (): any[] => {
    return classOptions.filter(c =>
      selectedClassIds.includes(c.id) &&
      c.sections &&
      c.sections.length > 0 &&
      (!selectedSections[c.id] || selectedSections[c.id].length === 0)
    );
  };

  // ── Toggle class selection ────────────────────────────────────────────────
  const toggleClass = (id: string) => {
    setSelectedClassIds(prev => {
      if (prev.includes(id)) {
        // Deselecting a class also clears any sections chosen for it
        setSelectedSections(sPrev => {
          if (!(id in sPrev)) return sPrev;
          const next = { ...sPrev };
          delete next[id];
          return next;
        });
        return prev.filter(c => c !== id);
      }
      return [...prev, id];
    });
  };

  // ── Toggle a section within a specific class ──────────────────────────────
  const toggleSection = (classId: string, section: string) => {
    setSelectedSections(prev => {
      const current = prev[classId] ?? [];
      const next = current.includes(section)
        ? current.filter(s => s !== section)
        : [...current, section];
      return { ...prev, [classId]: next };
    });
  };

  // ── Check if save should be disabled ──────────────────────────────────────
  const isSaveDisabled = useCallback(() => {
    if (saving || !selectedTeacher || selectedClassIds.length === 0 || !currentSession) {
      return true;
    }

    // Check if any selected class has sections but no sections selected
    const classesMissingSections = getClassesMissingSections();
    return classesMissingSections.length > 0;
  }, [saving, selectedTeacher, selectedClassIds, currentSession, selectedSections, classOptions]);

  // ── Save handler ─────────────────────────────────────────────────────────
  // Route fixed to POST /api/combined-form-teachers, and session_id/session are now
  // included since Go's CreateCombinedFormTeacher marks both `binding:"required"`.
  // `sections` is flattened across every selected class's chosen sections, since
  // the Go model stores one flat array for the whole assignment.
  const handleSave = async () => {
    // Validation 1: Teacher selected
    if (!selectedTeacher) {
      toast.current?.show({ severity: 'warn', summary: 'Validation', detail: 'Please select a teacher', life: 4000 });
      return;
    }

    // Validation 2: At least one class selected
    if (selectedClassIds.length === 0) {
      toast.current?.show({ severity: 'warn', summary: 'Validation', detail: 'Select at least one class', life: 4000 });
      return;
    }

    // Validation 3: For classes with sections, at least one section must be selected
    const classesMissingSections = getClassesMissingSections();
    if (classesMissingSections.length > 0) {
      const classNames = classesMissingSections.map(c => c.label).join(', ');
      toast.current?.show({
        severity: 'warn',
        summary: 'Validation',
        detail: `Please select at least one section for: ${classNames}`,
        life: 5000
      });
      return;
    }

    // Validation 4: Session exists
    if (!currentSession) {
      toast.current?.show({ severity: 'warn', summary: 'No Active Session', detail: 'Could not determine the current session for this school.', life: 4000 });
      return;
    }

    const flattenedSections = Array.from(
      new Set(Object.values(selectedSections).flat())
    );

    setSaving(true);
    try {
      const res = await apiClient.post('/api/combined-form-teachers', {
        teacher_id: selectedTeacher.id,
        session_id: currentSession.id,
        session:    currentSession.session,
        class_ids:  selectedClassIds,
        sections:   flattenedSections,
      });

      if (res.data?.status === 'error') throw new Error(res.data.message);

      toast.current?.show({ severity: 'success', summary: 'Saved', detail: `${selectedTeacher.name} assigned as Combined Form Teacher`, life: 3000 });

      // Reset form & refresh list
      setSelectedTeacher(null);
      setTeacherSearch('');
      setSelectedClassIds([]);
      setSelectedSections({});
      fetchEntries();
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.message || err?.message || 'Failed to save', life: 5000 });
    } finally {
      setSaving(false);
    }
  };

  // ── Delete (clear) a combined form teacher assignment ────────────────────
  // Go's DeleteCombinedFormTeacher deletes by the document's own _id (`entry.id`),
  // not by teacher_id — the old code passed teacher_id, which never matched.
  const handleDelete = async (entry: CombinedFormTeacherEntry) => {
    setDeletingKey(entry.id);
    try {
      const res = await apiClient.delete(`/api/combined-form-teachers/${entry.id}`);
      if (res.data?.status === 'error') throw new Error(res.data.message);
      toast.current?.show({ severity: 'success', summary: 'Removed', detail: `${entry.teacher_name} removed as Combined Form Teacher`, life: 3000 });
      fetchEntries();
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.message || 'Failed to remove', life: 5000 });
    } finally {
      setDeletingKey(null);
    }
  };

  // ── Loading skeleton ─────────────────────────────────────────────────────
  // BUG FIX: now also waits on schoolDetailsLoading so the "no active session"
  // banner doesn't flash before the school-details fetch has even resolved.
  const isLoading = loadingTeachers || loadingClasses || loadingEntries || schoolDetailsLoading;
  if (isLoading) {
    return (
      <Card style={{ textAlign: 'center', padding: '60px 24px' }}>
        <div style={{
          width: '38px', height: '38px',
          border: '3px solid #e2e8f0', borderTop: '3px solid #8b5cf6',
          borderRadius: '50%', animation: 'spin 0.9s linear infinite', margin: '0 auto 14px',
        }} />
        <p style={{ color: '#64748b', fontSize: '14px' }}>Loading combined form teacher settings…</p>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </Card>
    );
  }

  return (
    <>
      <Toast ref={toast} position="top-right" />

      {/* ── Assign Combined Form Teacher ──────────────────────────────────── */}
      <Card>
        <SectionHeader
          iconClass="pi pi-user-edit"
          iconBg="linear-gradient(135deg,#8b5cf6,#a78bfa)"
          title="Assign Combined Form Teacher"
          description="Assign a teacher as Combined Form Teacher for one or more classes, for the current session."
        />

        <InfoBanner>
          A Combined Form Teacher oversees multiple classes. Select the teacher and tick all classes they are responsible for. For classes that have sections, you can optionally narrow the assignment to specific sections.
          {currentSession ? ` Assignment will be saved for session "${currentSession.session}".` : ' No active session detected for this school — saving is disabled until one is available.'}
        </InfoBanner>

        <div style={{ marginTop: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>

          {/* ── Teacher picker ── */}
          <div>
            <label style={labelStyle}>Teacher</label>
            <div ref={teacherRef} style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="Search teacher by name or email…"
                value={selectedTeacher ? selectedTeacher.name : teacherSearch}
                onFocus={() => { setShowTeacherDropdown(true); if (selectedTeacher) setTeacherSearch(''); }}
                onChange={e => { setTeacherSearch(e.target.value); setSelectedTeacher(null); setShowTeacherDropdown(true); }}
                style={{ ...inputStyle, paddingRight: '36px' }}
              />
              {selectedTeacher && (
                <button
                  onClick={() => { setSelectedTeacher(null); setTeacherSearch(''); }}
                  style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}
                >
                  <i className="pi pi-times" style={{ fontSize: '12px' }} />
                </button>
              )}

              {showTeacherDropdown && !selectedTeacher && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 999,
                  background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px',
                  boxShadow: '0 8px 24px rgba(15,23,42,0.12)', maxHeight: '240px', overflowY: 'auto',
                  marginTop: '4px',
                }}>
                  {filteredTeachers.length === 0 ? (
                    <div style={{ padding: '14px 16px', fontSize: '13px', color: '#94a3b8' }}>No teachers found</div>
                  ) : filteredTeachers.map(t => (
                    <div
                      key={t.id}
                      onClick={() => { setSelectedTeacher(t); setTeacherSearch(t.name); setShowTeacherDropdown(false); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '10px',
                        padding: '10px 14px', cursor: 'pointer',
                        borderBottom: '1px solid #f8fafc',
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      <Avatar name={t.name} url={t.profile_picture_url} size={32} />
                      <div>
                        <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: '#0f172a' }}>{t.name}</p>
                        <p style={{ margin: 0, fontSize: '11px', color: '#64748b' }}>{t.email_id}</p>
                      </div>
                      <span style={{ marginLeft: 'auto', fontSize: '11px', color: '#cbd5e1', fontStyle: 'italic' }}>
                        {t.gender}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Selected teacher preview */}
            {selectedTeacher && (
              <div style={{
                marginTop: '10px', display: 'flex', alignItems: 'center', gap: '10px',
                background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: '10px', padding: '10px 14px',
              }}>
                <Avatar name={selectedTeacher.name} url={selectedTeacher.profile_picture_url} size={38} />
                <div>
                  <p style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: '#0f172a' }}>{selectedTeacher.name}</p>
                  <p style={{ margin: 0, fontSize: '11px', color: '#7c3aed' }}>{selectedTeacher.email_id}</p>
                </div>
                <span style={{
                  marginLeft: 'auto', background: '#ede9fe', color: '#6d28d9',
                  fontSize: '11px', fontWeight: '600', borderRadius: '20px', padding: '2px 10px',
                  border: '1px solid #c4b5fd',
                }}>
                  ✓ Selected
                </span>
              </div>
            )}
          </div>

          {/* ── Class + section picker ── */}
          <div>
            <label style={labelStyle}>
              Classes <span style={{ color: '#8b5cf6', fontWeight: '700' }}>({selectedClassIds.length} selected)</span>
            </label>
            {/* Single-column list (not a 2-col grid) so each selected class with
                sections can expand to show its section picker beneath it. */}
            <div style={{
              border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px',
              maxHeight: '260px', overflowY: 'auto', background: '#fafafa',
              display: 'flex', flexDirection: 'column', gap: '6px',
            }}>
              {classOptions.filter(c => c.id).length === 0 ? (
                <p style={{ fontSize: '13px', color: '#94a3b8', padding: '8px' }}>No classes available</p>
              ) : classOptions.filter(c => c.id).map(cls => {
                const checked = selectedClassIds.includes(cls.id);
                const hasSections = cls.sections && cls.sections.length > 0;
                const needsSections = checked && hasSections;
                const missingSections = needsSections && !hasSectionsSelected(cls.id);

                return (
                  <div key={cls.id} style={{
                    borderRadius: '8px',
                    background: checked ? '#f5f3ff' : '#fff',
                    border: `1px solid ${missingSections ? '#fca5a5' : checked ? '#c4b5fd' : '#e2e8f0'}`,
                    transition: 'all 0.15s',
                  }}>
                    <div
                      onClick={() => toggleClass(cls.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '7px 10px', cursor: 'pointer', userSelect: 'none',
                      }}
                    >
                      <div style={{
                        width: '16px', height: '16px', borderRadius: '4px', flexShrink: 0,
                        background: checked ? '#8b5cf6' : '#fff',
                        border: `2px solid ${checked ? '#8b5cf6' : '#cbd5e1'}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all 0.15s',
                      }}>
                        {checked && <i className="pi pi-check" style={{ fontSize: '9px', color: '#fff' }} />}
                      </div>
                      <span style={{
                        fontSize: '12px',
                        fontWeight: '600',
                        color: missingSections ? '#dc2626' : checked ? '#6d28d9' : '#334155'
                      }}>
                        {cls.label}
                        {missingSections && (
                          <span style={{ marginLeft: '6px', fontSize: '10px', color: '#dc2626', fontWeight: '400' }}>
                            ⚠️
                          </span>
                        )}
                      </span>
                      {hasSections && (
                        <span style={{ marginLeft: 'auto', fontSize: '10px', color: '#94a3b8' }}>
                          {cls.sections.length} section{cls.sections.length > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>

                    {/* Section picker — only shown once the class is checked and it has sections */}
                    {checked && hasSections && (
                      <div style={{
                        padding: '0 10px 10px 34px',
                      }}>
                        <div style={{
                          display: 'flex', flexWrap: 'wrap', gap: '6px',
                          marginBottom: '4px',
                        }}>
                          {cls.sections.map(sec => {
                            const secChecked = (selectedSections[cls.id] ?? []).includes(sec);
                            return (
                              <button
                                key={sec}
                                type="button"
                                onClick={(e) => { e.stopPropagation(); toggleSection(cls.id, sec); }}
                                style={{
                                  padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600',
                                  cursor: 'pointer',
                                  background: secChecked ? '#fef3c7' : '#fff',
                                  border: `1px solid ${secChecked ? '#fbbf24' : '#e2e8f0'}`,
                                  color: secChecked ? '#92400e' : '#64748b',
                                }}
                              >
                                {sec}
                              </button>
                            );
                          })}
                          <span style={{ fontSize: '10px', color: '#94a3b8', alignSelf: 'center' }}>
                            {(selectedSections[cls.id]?.length ?? 0) === 0 ? '(no sections picked = all sections)' : ''}
                          </span>
                        </div>

                        {/* Warning message when sections are required but none selected */}
                        {missingSections && (
                          <div style={{
                            fontSize: '11px',
                            color: '#dc2626',
                            fontWeight: '500',
                            marginTop: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                          }}>
                            <i className="pi pi-exclamation-triangle" style={{ fontSize: '11px' }} />
                            Please select at least one section for this class
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Selected class pills */}
            {selectedClassIds.length > 0 && (
              <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {selectedClassIds.map(id => {
                  const cls = classOptions.find(c => c.id === id);
                  return cls ? (
                    <ClassPill key={id} label={cls.label} onRemove={() => toggleClass(id)} />
                  ) : null;
                })}
                {Object.entries(selectedSections).flatMap(([classId, secs]) =>
                  secs.map(sec => (
                    <ClassPill
                      key={`${classId}-${sec}`}
                      tone="amber"
                      label={sec}
                      onRemove={() => toggleSection(classId, sec)}
                    />
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        {/* Save button */}
        <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          {(selectedTeacher || selectedClassIds.length > 0) && (
            <button
              onClick={() => { setSelectedTeacher(null); setTeacherSearch(''); setSelectedClassIds([]); setSelectedSections({}); }}
              style={{
                padding: '9px 18px', borderRadius: '8px', fontSize: '13px', fontWeight: '600',
                background: '#fff', border: '1px solid #e2e8f0', color: '#64748b', cursor: 'pointer',
              }}
            >
              Clear
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={isSaveDisabled()}
            style={{
              display: 'flex', alignItems: 'center', gap: '7px',
              padding: '9px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: '600',
              background: isSaveDisabled() ? '#e2e8f0' : 'linear-gradient(135deg,#8b5cf6,#7c3aed)',
              color: isSaveDisabled() ? '#94a3b8' : '#fff',
              border: 'none', cursor: isSaveDisabled() ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
            }}
          >
            <i className={saving ? 'pi pi-spin pi-spinner' : 'pi pi-check'} style={{ fontSize: '13px' }} />
            {saving ? 'Saving…' : 'Save Assignment'}
          </button>
        </div>
      </Card>

      {/* ── Existing Combined Form Teachers ───────────────────────────────── */}
      <Card>
        <SectionHeader
          iconClass="pi pi-users"
          iconBg="linear-gradient(135deg,#0ea5e9,#38bdf8)"
          title="Assigned Combined Form Teachers"
          description="All teachers currently assigned as Combined Form Teachers for the current session"
          controls={
            <button
              onClick={fetchEntries}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '7px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: '600',
                background: '#f8fafc', border: '1px solid #e2e8f0', color: '#475569', cursor: 'pointer',
              }}
            >
              <i className="pi pi-refresh" style={{ fontSize: '12px' }} />
              Refresh
            </button>
          }
        />

        {entries.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 24px' }}>
            <div style={{
              width: '52px', height: '52px', borderRadius: '14px',
              background: 'linear-gradient(135deg,#f1f5f9,#e2e8f0)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px',
            }}>
              <i className="pi pi-users" style={{ fontSize: '22px', color: '#94a3b8' }} />
            </div>
            <p style={{ fontSize: '14px', fontWeight: '600', color: '#334155', margin: '0 0 4px' }}>No assignments yet</p>
            <p style={{ fontSize: '12px', color: '#94a3b8', margin: 0 }}>Assign a teacher using the form above</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {entries.map(entry => {
              const isDeleting = deletingKey === entry.id;
              // Cross-reference the already-loaded teachers list for email/avatar,
              // since the Go aggregation doesn't join sp_users for those fields.
              const teacherInfo = teachers.find(t => t.id === entry.teacher_id);

              return (
                <div
                  key={entry.id}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: '14px',
                    padding: '14px 16px', borderRadius: '12px',
                    background: '#fafafa', border: '1px solid #e2e8f0',
                    transition: 'border-color 0.2s',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.borderColor = '#c4b5fd')}
                  onMouseLeave={e => (e.currentTarget.style.borderColor = '#e2e8f0')}
                >
                  <Avatar name={entry.teacher_name} url={teacherInfo?.profile_picture_url} size={42} />

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <p style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>{entry.teacher_name}</p>
                      <span style={{
                        fontSize: '11px', fontWeight: '600', borderRadius: '20px', padding: '2px 10px',
                        background: '#f5f3ff', color: '#7c3aed', border: '1px solid #ddd6fe',
                      }}>
                        Combined Form Teacher
                      </span>
                    </div>
                    {teacherInfo?.email_id && (
                      <p style={{ margin: '2px 0 8px', fontSize: '11px', color: '#64748b' }}>{teacherInfo.email_id}</p>
                    )}

                    {/* Class pills — the Go backend already resolves class names for us */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                      {entry.classes.length > 0 ? (
                        entry.classes.map(c => <ClassPill key={c.class_id} label={c.class_name} />)
                      ) : (
                        <span style={{ fontSize: '12px', color: '#94a3b8' }}>No classes assigned</span>
                      )}
                    </div>

                    {/* Section pills, if this assignment was narrowed to specific sections */}
                    {entry.sections && entry.sections.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginTop: '6px' }}>
                        {entry.sections.map(sec => (
                          <ClassPill key={sec} label={`Sec. ${sec}`} tone="amber" />
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Edit shortcut + delete */}
                  <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                    <button
                      onClick={() => {
                        const teacher = teachers.find(t => t.id === entry.teacher_id);
                        if (teacher) {
                          setSelectedTeacher(teacher);
                          setTeacherSearch(teacher.name);
                          setSelectedClassIds(entry.class_ids);
                          // Best-effort restore: the backend only stores one flat
                          // sections array, so we can only map it back to a specific
                          // class when exactly one class is on the assignment.
                          if (entry.sections && entry.sections.length > 0 && entry.class_ids.length === 1) {
                            setSelectedSections({ [entry.class_ids[0]]: entry.sections });
                          } else {
                            setSelectedSections({});
                          }
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }
                      }}
                      title="Edit"
                      style={{
                        width: '32px', height: '32px', borderRadius: '8px',
                        background: '#eff6ff', border: '1px solid #bfdbfe',
                        color: '#3b82f6', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      <i className="pi pi-pencil" style={{ fontSize: '12px' }} />
                    </button>
                    <button
                      onClick={() => handleDelete(entry)}
                      disabled={isDeleting}
                      title="Remove"
                      style={{
                        width: '32px', height: '32px', borderRadius: '8px',
                        background: '#fff1f2', border: '1px solid #fecdd3',
                        color: isDeleting ? '#fca5a5' : '#ef4444',
                        cursor: isDeleting ? 'not-allowed' : 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      <i className={isDeleting ? 'pi pi-spin pi-spinner' : 'pi pi-trash'} style={{ fontSize: '12px' }} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </>
  );
};

export default CombinedFormTeacherSettings;